import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortable, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ContextMenuTriggerDirective } from 'src/directives/context-menu-trigger.directive';
import { Entry } from 'src/services/entry/Entry';
import { DetailedEntry } from 'src/services/entry/EntryWithDetails';
import { Period, Tag } from 'src/services/tag/Tag';
import { TagNodeSummaryComponent } from '../tag-node-summary/tag-node-summary.component';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { TagListComponent } from 'src/components/tag-list/tag-list.component';
import { BrowserService } from 'src/services/browser/browser.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { EditEntryBottomSheetComponent } from 'src/app/components/edit-entry-bottom-sheet/edit-entry-bottom-sheet.component';
import { TagNodeIconComponent } from '../tag-node-icon/tag-node-icon.component';
import { getTagColor } from 'src/utils/getTagColor';
import { trackByTagId } from 'src/utils/trackByTagId';
import { isParent } from 'src/utils/isParent';
import { WelcomeToPrefundComponent } from '../../app/components/welcome-to-prefund/welcome-to-prefund.component';
import { PrefundCurrencyPipe } from 'src/app/prefund-currency.pipe';
import { LocalSettingsService } from 'src/app/services/local-settings.service';
import { PeriodService } from 'src/app/services/period.service';
import { isWithinCurrentPeriod } from 'src/utils/isWithinCurrentPeriod';

@Component({
  selector: 'entries',
  templateUrl: './entries.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    ContextMenuTriggerDirective,
    TagNodeSummaryComponent,
    TagListComponent,
    MatIconModule,
    TagNodeIconComponent,
    WelcomeToPrefundComponent,
    PrefundCurrencyPipe,
  ],
  standalone: true,
  styleUrls: ['./entries.component.scss'],
})
export class EntriesComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = this.browserService.isOnMobile()
    ? ['amount', 'date', 'memo', 'tags']
    : ['date', 'amount', 'memo', 'tags'];

  @Input() set tag(tag: string) {
    this.setupSorting();
    this._tag = tag;
    this.filter();
  }

  _tag: string = 'Entries';

  get tag() {
    return this._tag;
  }

  @Input()
  tagsWithSummaryDetails: DetailedTag[] = [];

  @Input()
  plannedExpenditure: number = 0;

  applyPeriodFilter = true;

  @Input()
  set periodFilter(value: Period | null) {
    this._periodFilter = value;
    this.filter();
  }

  get periodFilter() {
    return this._periodFilter;
  }

  private _periodFilter: Period | null = null;

  @Output()
  onDelete = new EventEmitter<Entry>();

  @Output()
  onPlannedAmountClicked = new EventEmitter<Tag>();

  @Output()
  onHamburgerClick = new EventEmitter<void>();

  @Output()
  onEdit = new EventEmitter<Entry>();

  @Output() onOpenPlannerClick = new EventEmitter<string>();
  @Output() onOpenAdderClick = new EventEmitter<void>();

  private _bottomSheet = inject(MatBottomSheet);

  _detailedEntries: DetailedEntry[] = [];

  get detailedEntries() {
    return this._detailedEntries;
  }

  dataSource: MatTableDataSource<DetailedEntry> | null = null;

  @ViewChild(MatSort) sort: MatSort | null = null;

  showTagsOnMobile = this.localSettingsService.settings.showTagsOnMobile;

  getTagColor = getTagColor;
  trackByTagId = trackByTagId;

  filteredDetailedEntries: DetailedEntry[] = [];

  @Input() set detailedEntries(detailedEntries: DetailedEntry[]) {
    this._detailedEntries = detailedEntries;
    this.filter();
  }

  // TODO: this works in a weird way
  sortByDate() {
    this.dataSource = new MatTableDataSource(
      // @ts-ignore
      this.detailedEntries.sort((a: DetailedEntry, b: DetailedEntry) => {
        return a.date && b.date
          ? new Date(b.date).getTime() - new Date(a.date).getTime()
          : 0;
      })
      // null
    );
  }

  filter() {
    const detailedEntriesWithPeriodFilter = this.detailedEntries.filter(
      (e) =>
        !this.getTag(this.tag)?.isPeriodic ||
        !this.applyPeriodFilter ||
        isWithinCurrentPeriod(e, this.periodService.current)
    );

    this.filteredDetailedEntries = detailedEntriesWithPeriodFilter.filter(
      (entry) =>
        !this.tag ||
        entry.tags.some(
          (tag) => tag.fullPath === this.tag || isParent(this.tag, tag.fullPath)
        )
    );

    this.dataSource = new MatTableDataSource(this.filteredDetailedEntries);
    this.dataSource.sort = this.sort;
    // this.changeDetectorRef.detectChanges();
  }

  setupSorting() {
    // On page load, sorting is not working, this is a hacky way to fix this
    const entries = [...this.detailedEntries];
    this.detailedEntries = [];
    this.detailedEntries = entries;

    this.dataSource!.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'date':
          return new Date(item?.date ?? 0).getTime();
        case 'amount':
          return item.amount;
        case 'memo':
          return item.memo ?? '';
        case 'tags':
          return item.tags.map((t) => t.fullPath).join(' ');
        default:
          return 0;
      }
    };
  }

  constructor(
    public matDialog: MatDialog,
    public router: Router,
    public browserService: BrowserService,
    private changeDetectorRef: ChangeDetectorRef,
    private localSettingsService: LocalSettingsService,
    public periodService: PeriodService
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.filter(), 0);
  }

  ngAfterViewInit(): void {
    this.setupSorting();
    this.sortByDate();
  }

  public get actualExpenditure(): number {
    return this.detailedEntries.reduce((acc, entry) => acc + entry.amount, 0);
  }

  openAddEntryDialog() {
    this.onOpenAdderClick.emit();
  }

  delete(row: Entry) {
    this.onDelete.emit(row);
  }

  edit(row: Entry) {
    this.onEdit.emit(row);
  }

  navigateToTag(tag: Tag, event: Event) {
    event.stopPropagation();
    this.router.navigate(['/tags/' + tag.fullPath]);
  }

  openPlannerDialog() {
    this.onOpenPlannerClick.emit(
      this.tagsWithSummaryDetails.find((t) => t.fullPath === this.tag)?.id
    );
  }

  getTopLevelTag = getTopLevelTag;

  getTag(tag: string): DetailedTag {
    return this.tagsWithSummaryDetails.find((t) => t.fullPath === tag)!;
  }

  toggleShowTagsOnMobile() {
    this.showTagsOnMobile = !this.showTagsOnMobile;
    this.localSettingsService.set({
      ...this.localSettingsService.settings,
      showTagsOnMobile: this.showTagsOnMobile,
    });
  }

  openEditEntryBottomSheet(entry: DetailedEntry) {
    this._bottomSheet.open(EditEntryBottomSheetComponent, {
      data: {
        entry: { ...entry },
        tags: [...this.tagsWithSummaryDetails],
      },
    });
  }
}
